import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Select from "@material-ui/core/Select";
import MuiMenuItem from "@material-ui/core/MenuItem";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import InputLabel from "@material-ui/core/InputLabel";
import Fade from "@material-ui/core/Fade";
import TextField from "@material-ui/core/TextField";
import React from "react";
import { Redirect } from "react-router-dom";
import styled from "styled-components";

import db from "js/db";
import { getStaticUrl } from "js/config";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";
import { trackActivity } from "js/core/utilities/utilities";
import { FormIcon, StandalonePage, StandaloneForm } from "./Standalone";

const baiBotImage = getStaticUrl("/images/onboarding/bai-bot.gif");

const StyledSelect = styled(Select)`
    border: 1px solid #d8d8d8;
    border-radius: 2px;
    font-size: 16px;

    .MuiSelect-select {
        text-transform: none;
        font-weight: normal;
        font-size: 16px;
    }
`;

const MenuItem = styled(MuiMenuItem)`
    font-weight: normal;
`;

const PlaceholderText = styled.span`
    color: #a2a2a2;
    font-variant: normal;
    font-weight: normal;
`;

const SurveyDialogTitle = styled(DialogTitle)`
    &.MuiDialogTitle-root {
        padding-top: 0;
        padding-bottom: 0;
    }
`;

const SurveyDialogContent = styled(DialogContent)`
    &.MuiDialogContent-root {
        padding: 20px 40px;
    }
`;

const SurveyDialogActions = styled(DialogActions)`
    &.MuiDialogActions-root {
        padding-bottom: 20px;
    }
`;

const SurveySelect = ({ placeholder, children, ...props }) => {
    return (
        <StyledSelect
            disableUnderline
            displayEmpty
            fullWidth
            {...props}
        >
            <MenuItem value="" disabled>
                <PlaceholderText>{placeholder}</PlaceholderText>
            </MenuItem>
            {children}
        </StyledSelect>
    );
};

const SurveyLabel = styled(InputLabel)`
    &.MuiFormLabel-root {
        font-weight: bold;
        font-size: 95%;
        color: rgba(0, 0, 0, 0.87);
        padding-bottom: 0.6em;
        margin-top: 20px;
    }
`;

export const MarketingSurvery = withFirebaseUser(class MarketingSurveryImpl extends React.Component {
    requiredFields = [
        "userType",
        "companySize",
        "userRole"
    ];

    state = {
        fadeOut: false,
        answers: {
            version: 2
        }
    };

    async componentDidMount() {
        const userInfoSnap = await db.getDbInstance("users").ref(`users/${this.props.firebaseUser.uid}/userInfo`).once("value");
        if (userInfoSnap.exists()) {
            const userInfo = userInfoSnap.val();
            console.log(userInfo);
            this.setState({
                answers: userInfo
            });
        }
    }

    handleChange(key, value) {
        this.setState(state => {
            const newAnswers = {
                ...state.answers,
                [key]: value,
            };

            const isComplete = this.requiredFields
                .filter(key => newAnswers[key] && newAnswers[key] != "")
                .length == this.requiredFields.length;

            if (isComplete) {
                newAnswers.complete = true;
            }

            return {
                ...state,
                answers: newAnswers,
                isComplete
            };
        });
    }

    handleSubmit = async () => {
        await db.getDbInstance("users").ref(`users/${this.props.firebaseUser.uid}`).update({
            userInfo: this.state.answers
        });

        if (this.state.isComplete) {
            trackActivity("OnboardingSurvey", "Complete", null, null, { survery: this.state.answers });
        } else {
            trackActivity("OnboardingSurvey", "Skip");
        }

        this.continue();
    }

    continue = () => {
        this.setState({ fadeOut: true });
        setTimeout(async () => {
            this.props.nextStep();
        }, 750);
    }

    render() {
        return (
            <StandalonePage maxWidth={600}>
                <Fade in={!this.state.fadeOut} timeout={750}>
                    <StandaloneForm
                        open={true}
                        hideBackdrop
                        fullWidth
                    >
                        <FormIcon src={baiBotImage} />

                        <SurveyDialogTitle align="center">
                            A little about you
                        </SurveyDialogTitle>

                        <Typography variant="subtitle1" align="center" style={{ color: "gray" }}>
                            Before getting started, we have just a few quick questions.
                        </Typography>

                        <SurveyDialogContent>
                            <Grid container spacing={1}>
                                <Grid item container xs={12}>
                                    <SurveyLabel htmlFor="userType">What will you be using Beautiful.ai for?</SurveyLabel>
                                    <SurveySelect
                                        labelId="userType"
                                        placeholder="Select an option *"
                                        value={this.state.answers["userType"] || ""}
                                        onChange={e => this.handleChange("userType", e.target.value)}
                                    >
                                        <MenuItem value="work">Work</MenuItem>
                                        <MenuItem value="academic">Academic</MenuItem>
                                        <MenuItem value="personal">Personal</MenuItem>
                                    </SurveySelect>
                                </Grid>

                                <Grid item container xs={12} sm={6}>
                                    <TextField
                                        id="companyName"
                                        placeholder="Company Name"
                                        variant="outlined"
                                        value={this.state.answers["companyName"] || ""}
                                        onChange={e => this.handleChange("companyName", e.target.value)}
                                        fullWidth
                                    />
                                </Grid>

                                <Grid item container xs={12} sm={6}>
                                    <SurveySelect
                                        labelId="companySize"
                                        placeholder="Company Size *"
                                        value={this.state.answers["companySize"] || ""}
                                        onChange={e => this.handleChange("companySize", e.target.value)}
                                    >
                                        <MenuItem value="1">Just Me</MenuItem>
                                        <MenuItem value="2-10">2–10</MenuItem>
                                        <MenuItem value="11-50">11–50</MenuItem>
                                        <MenuItem value="51-250">51–250</MenuItem>
                                        <MenuItem value="250-1000">250–1000</MenuItem>
                                        <MenuItem value="1000+">1000+</MenuItem>
                                    </SurveySelect>
                                </Grid>

                                <Grid item container xs={12}>
                                    <SurveySelect
                                        labelId="userRole"
                                        placeholder="Role *"
                                        value={this.state.answers["userRole"] || ""}
                                        onChange={e => this.handleChange("userRole", e.target.value)}
                                    >
                                        <MenuItem value="business-development">Business Development</MenuItem>
                                        <MenuItem value="customer-success">Customer Success / Support</MenuItem>
                                        <MenuItem value="design">Design</MenuItem>
                                        <MenuItem value="founder-ceo">Founder / CEO</MenuItem>
                                        <MenuItem value="hr">Human Resources</MenuItem>
                                        <MenuItem value="manager-director-cxo">Manager / Director / CxO</MenuItem>
                                        <MenuItem value="marketing">Marketing</MenuItem>
                                        <MenuItem value="operations">Operations</MenuItem>
                                        <MenuItem value="product-engineering">Product / Engineering</MenuItem>
                                        <MenuItem value="sales">Sales</MenuItem>
                                        <MenuItem value="staff-admin">Staff / Administrator</MenuItem>
                                        <MenuItem value="student">Student</MenuItem>
                                        <MenuItem value="teacher-professor">Teacher / Professor</MenuItem>
                                    </SurveySelect>
                                </Grid>
                            </Grid>

                            <SurveyLabel htmlFor="presentationFrequency">How often do you create presentations?</SurveyLabel>
                            <SurveySelect
                                labelId="presentationFrequency"
                                placeholder="Select an option"
                                value={this.state.answers["presentationFrequency"] || ""}
                                onChange={e => this.handleChange("presentationFrequency", e.target.value)}
                            >
                                <MenuItem value="daily">Daily</MenuItem>
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="monthly">A few times per month</MenuItem>
                                <MenuItem value="annually">A few times per year</MenuItem>
                                <MenuItem value="rarely">Rarely</MenuItem>
                            </SurveySelect>

                            <SurveyLabel htmlFor="contentType">What best describes the content you plan on creating?</SurveyLabel>
                            <SurveySelect
                                labelId="contentType"
                                placeholder="Select an option"
                                value={this.state.answers["contentType"] || ""}
                                onChange={e => this.handleChange("contentType", e.target.value)}
                            >
                                <MenuItem value="external">Fundraising and portfolio</MenuItem>
                                <MenuItem value="sales">Sales pitches and service proposals</MenuItem>
                                <MenuItem value="documentation">Process, training, and onboarding</MenuItem>
                                <MenuItem value="meetings">Team and company meetings</MenuItem>
                                <MenuItem value="conferences">Conferences, webinars, and events</MenuItem>
                                <MenuItem value="teaching">Lectures and instructional</MenuItem>
                                <MenuItem value="school">School and course reports</MenuItem>
                                <MenuItem value="presentation">A single presentation</MenuItem>
                                <MenuItem value="graphic">A single graphic or infographic</MenuItem>
                                <MenuItem value="none">Nothing - Just checking it out</MenuItem>
                            </SurveySelect>

                            <SurveyLabel htmlFor="channel">How did you hear about Beautiful.ai?</SurveyLabel>
                            <SurveySelect
                                labelId="channel"
                                placeholder="Select an option"
                                value={this.state.answers["channel"] || ""}
                                onChange={e => this.handleChange("channel", e.target.value)}
                            >
                                <MenuItem value="search">Search</MenuItem>
                                <MenuItem value="recommendation">Recommended by friend or colleague</MenuItem>
                                <MenuItem value="social">Social media</MenuItem>
                                <MenuItem value="blog">Blog or article</MenuItem>
                                <MenuItem value="viewed">Saw a Beautiful.ai presentation</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                            </SurveySelect>
                        </SurveyDialogContent>

                        <SurveyDialogActions>
                            {!this.state.isComplete && (
                                <Button
                                    color="secondary"
                                    onClick={this.handleSubmit}
                                    style={{ marginRight: "auto", color: "rgba(0, 0, 0, 0.26)" }}
                                >
                                    Ask me later
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={this.handleSubmit}
                                disabled={!this.state.isComplete}
                            >
                                Done!
                            </Button>
                        </SurveyDialogActions>

                        {this.state.redirect && (
                            <Redirect to={this.state.redirect} />
                        )}
                    </StandaloneForm>
                </Fade>
            </StandalonePage>
        );
    }
});
